import { clear, el } from './dom';
import { icon } from './Icons';
import {
  FLORA_LENSES,
  describeLensSummary,
  lensById,
  type LensDefinition,
  type LensId,
  type LensSummary,
} from './FloraLens';
import type { UiPreferences } from './UiPreferences';

/**
 * The lens bar: the strip along the foot of the design view.
 *
 * ## Why it lives over the view rather than in a tray
 *
 * A lens is not a setting. It is a way of *looking at what is in front of you*, and it changes the
 * canvas the moment it is picked — so it belongs in the same glance as the thing it changes, the way
 * the site bar sits directly above the palette whose badges it rewrites. The HUD's centre column is
 * the one element whose box is the view itself and which is transparent to the pointer, so a bar
 * mounted in it stays clear of both docks at every width and leaves with the rest of the chrome when
 * photo mode clears the frame.
 *
 * ## Why the plan map is on it
 *
 * The plan map answers "where" for the planting; a lens answers "where" for one property of it. They
 * are the same question at two levels of abstraction, and having one reached from the Manifest menu
 * and the other from a bar at the foot of the screen would have split that in half. So the bar opens
 * the plain plan map *and* the lensed one, and the Manifest menu's own entry keeps working exactly as
 * it did — the map is still owned by the manifest panel, and this only asks it to open.
 *
 * ## The caption
 *
 * The line above the chips is the part that stops the lens lying by omission. An empty heat map is
 * ambiguous between "you planted nothing for this" and "come back in May", and those want opposite
 * responses from a designer, so {@link describeLensSummary} says which it is.
 */

export interface SiteLensBarDeps {
  preferences: UiPreferences;
  /** Repaint: recompute the readings and push them to the 3D view. */
  onLensChange: () => void;
  /** Open the plan map. `null` opens it plain; a lens id opens it reading that lens. */
  onOpenMap: (lens: LensId | null) => void;
  /** The site's reading of the active lens, for the caption. Null when no lens is active. */
  summary: () => LensSummary | null;
}

export class SiteLensBar {
  readonly root: HTMLElement;

  private active: LensDefinition | null;
  private peakMode: boolean;

  private readonly caption: HTMLElement;
  private readonly chips: HTMLElement;
  private readonly modeGroup: HTMLElement;
  private readonly mapButton: HTMLButtonElement;

  constructor(private readonly deps: SiteLensBarDeps) {
    // A lens retired since the preference was written resolves to null rather than throwing, which
    // is the same tolerance `shutPanels` shows for an unrecognised palette name.
    this.active = deps.preferences.lens ? lensById(deps.preferences.lens as LensId) : null;
    this.peakMode = deps.preferences.lensPeak;

    this.caption = el('p', { class: 'lg-sitelens__caption' });
    this.chips = el('div', {
      class: 'lg-sitelens__chips',
      attrs: { role: 'group', 'aria-label': 'Site lenses' },
    });
    this.modeGroup = el('div', {
      class: 'lg-segment lg-segment--tight lg-sitelens__mode',
      attrs: { role: 'group', 'aria-label': 'When to read the lens' },
    });

    this.mapButton = el('button', {
      class: 'lg-sitelens__button lg-sitelens__button--map',
      html: `${icon('top', 15)}<span>Plan map</span>`,
      attrs: { type: 'button' },
      on: { click: () => this.deps.onOpenMap(this.active?.id ?? null) },
    });

    this.buildChips();
    this.buildMode();

    this.root = el(
      'div',
      { class: 'lg-sitelens', attrs: { role: 'toolbar', 'aria-label': 'Site lenses' } },
      this.caption,
      el('div', { class: 'lg-sitelens__row' }, this.mapButton, this.chips, this.modeGroup),
    );

    this.refresh();
  }

  /** The lens in force, or null. The renderers ask this rather than being told. */
  get lens(): LensDefinition | null {
    return this.active;
  }

  /** Whether readings are taken at each plant's best day rather than at the date on the chrono bar. */
  get peak(): boolean {
    return this.peakMode;
  }

  /** Repaint the caption and the pressed states. Cheap enough for every editor change. */
  refresh(): void {
    for (const button of this.chips.querySelectorAll('button')) {
      const on = button.dataset.lens === this.active?.id;
      button.classList.toggle('is-active', on);
      button.setAttribute('aria-pressed', String(on));
    }

    // Meaningless without a lens, and actively misleading with a timeless one: there is no "today"
    // for an overall value, and offering the toggle would imply the reading moves when it does not.
    const timed = this.active !== null && this.active.timeless !== true;
    this.modeGroup.hidden = !timed;
    for (const button of this.modeGroup.querySelectorAll('button')) {
      const on = (button.dataset.mode === 'peak') === this.peakMode;
      button.classList.toggle('is-active', on);
      button.setAttribute('aria-pressed', String(on));
    }

    const mapLabel = this.mapButton.querySelector('span');
    if (mapLabel) mapLabel.textContent = this.active ? 'Plan map · lensed' : 'Plan map';
    this.mapButton.title = this.active
      ? `Plot the ${this.active.label.toLowerCase()} lens on a top-down plan`
      : 'Plot the planting on a top-down plan';

    this.root.classList.toggle('has-lens', this.active !== null);
    this.drawCaption();
  }

  dispose(): void {
    this.root.remove();
  }

  /* ---------------------------------- The controls ---------------------------------- */

  private buildChips(): void {
    clear(this.chips);
    for (const lens of FLORA_LENSES) {
      this.chips.appendChild(
        el('button', {
          class: 'lg-sitelens__chip',
          title: lens.question,
          html: `${icon(lens.icon, 15)}<span>${lens.label}</span>`,
          attrs: { type: 'button', 'aria-pressed': false },
          dataset: { lens: lens.id },
          on: { click: () => this.pick(lens) },
        }),
      );
    }
  }

  private buildMode(): void {
    for (const [mode, label, hint] of [
      ['today', 'Today', 'Read every plant on the date the chrono bar is showing'],
      ['peak', 'Peak', "Read every plant at its own best day — the plan, rather than the moment"],
    ] as const) {
      this.modeGroup.appendChild(
        el('button', {
          class: 'lg-segment__item',
          text: label,
          title: hint,
          attrs: { type: 'button' },
          dataset: { mode },
          on: {
            click: () => {
              this.peakMode = mode === 'peak';
              this.deps.preferences.setLensPeak(this.peakMode);
              this.deps.onLensChange();
              this.refresh();
            },
          },
        }),
      );
    }
  }

  /** Picking the lens already in force turns it off — the chip is the switch, both ways. */
  private pick(lens: LensDefinition): void {
    this.active = this.active?.id === lens.id ? null : lens;
    this.deps.preferences.setLens(this.active?.id ?? null);
    this.deps.onLensChange();
    this.refresh();
  }

  private drawCaption(): void {
    if (!this.active) {
      this.caption.hidden = true;
      return;
    }
    const summary = this.deps.summary();
    if (!summary) {
      this.caption.hidden = true;
      return;
    }
    this.caption.hidden = false;
    clear(this.caption);
    this.caption.appendChild(
      el('span', { class: 'lg-sitelens__question', text: this.active.question }),
    );
    this.caption.appendChild(
      el('span', {
        class: `lg-sitelens__reading${summary.offering === 0 ? ' is-empty' : ''}`,
        text: describeLensSummary(this.active, summary, this.peakMode),
      }),
    );
  }
}
